import { useState } from 'react'

import Styles from './Photos.module.scss'
import ImageMenuModalStyles from '../ImageMenuModal.module.scss'

import { Modal } from 'almanac-ui/Modal'
import { useUnsplashImages } from 'hooks/queries/useUnsplashImages'
import { useDebounce } from 'hooks/useDebounce'
import { SearchField } from 'components/ImagePickerModal/SearchField/SearchField'
import { Grid } from 'components/ImagePickerModal/Grid/Grid'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { ScrollContainer } from 'components/ImagePickerModal/ScrollContainer/ScrollContainer'
import { ImageThumbnail } from 'components/ImagePickerModal/ImageThumbnail/ImageThumbnail'
import { usePixabayElements } from 'hooks/queries/designer/usePixabayElements'
import { SelectedImage } from 'types'
import { useImageMenuContext } from '../ImageMenuContext'
import { Actions } from '../ImageMenuModal'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

export const Photos = () => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { selectedImages, toggleSelectedImage, setProvider, initialQuery } = useImageMenuContext()
  const [query, setQuery] = useState(initialQuery || '')
  const debouncedQuery = useDebounce(query, 500)
  const {
    data: unsplashData,
    fetchNextPage: unsplashFetchNextPage,
    hasNextPage: unsplashHasNextPage,
  } = useUnsplashImages({ query: debouncedQuery, perPage: 24 })
  const {
    data: pixabayData,
    fetchNextPage: pixabayFetchNextPage,
    hasNextPage: pixabayHasNextPage,
  } = usePixabayElements({ query: debouncedQuery, pageSize: 24, imageType: 'all' })
  const unsplashResults = (unsplashData?.pages || []).flatMap((page) =>
    page?.type === 'success' ? (page.data.results || page.data) ?? [] : [],
  )
  const unsplashImages = unsplashResults.map((item) => {
    return {
      url: item.urls.regular,
      height: item.height,
      width: item.width,
      unsplash: {
        authorUsername: item.user.username,
        authorName: item.user.name,
        id: item.id,
        query,
      },
    }
  })
  const pixabayResults = (pixabayData?.pages || []).flatMap((page) => page.images || [])
  const pixabayImages = pixabayResults.map((item) => {
    return {
      url: item.largeImageURL,
      height: item.imageHeight,
      width: item.imageWidth,
      pixabay: {
        id: item.id,
      },
    }
  })

  const images: SelectedImage[] = []
  const maxLength = Math.max(unsplashImages.length, pixabayImages.length)

  for (let i = 0; i < maxLength; i++) {
    if (i < unsplashImages.length) {
      images.push(unsplashImages[i])
    }
    if (i < pixabayImages.length) {
      images.push(pixabayImages[i])
    }
  }

  const hasNextPage = unsplashHasNextPage || pixabayHasNextPage
  const fetchNextPage = () => {
    unsplashFetchNextPage()
    pixabayFetchNextPage()
  }

  return (
    <>
      <Modal.Header
        className={ImageMenuModalStyles.header}
        actions={<Actions />}
        title="Insert Photos"
        onBack={() => setProvider('')}
      />
      <Modal.Content className={Styles.content} withoutFooter={isSystemRedesignEnabled}>
        <div className={Styles.searchContainer}>
          <SearchField value={query} onChange={setQuery} />
        </div>
        <div className={Styles.photos} data-testid="photos-provider">
          <ScrollContainer>
            <Grid.Root breakpointCols={4}>
              {[
                ...Array.from({ length: images.length === 0 ? 12 : 0 }).map((_, i) => (
                  <Grid.Thumbnail key={`initial-loading-${i}`}>
                    <ShimmeringColor aspectRatio={Math.random() + 0.5} width="100%" />
                  </Grid.Thumbnail>
                )),
                ...images.map((image) => (
                  <Grid.Thumbnail key={image.unsplash?.id || image.pixabay?.id}>
                    <ImageThumbnail
                      image={image}
                      onToggleSelect={() => toggleSelectedImage({ ...image, provider: 'stockPhoto' })}
                      isSelected={selectedImages.some((i) => i.url === image.url)}
                      label={image.unsplash?.authorName}
                    />
                  </Grid.Thumbnail>
                )),
                ...Array.from({ length: hasNextPage ? 12 : 0 }).map((_, i) => (
                  <Grid.Thumbnail key={`scroll-loading-${i}`}>
                    <ShimmeringColor aspectRatio={Math.random() + 0.5} width="100%" />
                  </Grid.Thumbnail>
                )),
                <InfiniteScroll hasMore={!!hasNextPage} loadMore={fetchNextPage} key="infinite-scroll" />,
              ]}
            </Grid.Root>
          </ScrollContainer>
        </div>
      </Modal.Content>
    </>
  )
}
